import {
  Settings,
  LineChart,
  BarChart3,
  Sparkles,
  Wallet,
  Layers,
  ShoppingCart,
  ArrowRightLeft,
  Banknote,
  ClipboardCheck,
  Truck,
  ShieldAlert,
  Megaphone,
  Activity,
} from 'lucide-react';
import type { ComponentType } from 'react';
import {
  DcAccountsIcon,
  DcAppointmentListIcon,
  DcAppointmentRequestsIcon,
  DcAppointmentsIcon,
  DcBalanceSheetIcon,
  DcBatchesIcon,
  DcBranchesSideBySideIcon,
  DcCalendarIcon,
  DcCashControlIcon,
  DcCashFlowIcon,
  DcCategoryAtChairIcon,
  DcCategoryClinicIcon,
  DcCategoryFrontDeskIcon,
  DcChatIcon,
  DcClaimsIcon,
  DcCompaniesPlansIcon,
  DcEquipmentAssetsIcon,
  DcExpenseIcon,
  DcFinanceAccountsIcon,
  DcFinanceOverviewIcon,
  DcIncomeIcon,
  DcInstalmentsIcon,
  DcInsuranceClaimsIcon,
  DcInsuranceOverviewIcon,
  DcInventoryIcon,
  DcLabPartnerIcon,
  DcLabRequestsIcon,
  DcLabTrackingIcon,
  DcMoneyOwedToYouIcon,
  DcMonthlyFinancialReportIcon,
  DcOffersLoyaltyIcon,
  DcOverviewIcon,
  DcOwnerLoansIcon,
  DcPatientFileIcon,
  DcPatientStatementIcon,
  DcPayrollCommissionIcon,
  DcProductionByDentistIcon,
  DcProfitLossIcon,
  DcProfitPerTreatmentIcon,
  DcRecallsIcon,
  DcRefundsCorrectionsIcon,
  DcReportsAnalyticsIcon,
  DcTransactionsIcon,
  DcWaitlistIcon,
  DcWhatEachPatientSpentIcon,
  DcWhatItCostsToRunIcon,
  DcWhichTreatmentsMakeMoneyIcon,
} from '@/components/ds/icons/DentaCaresIcons';
import type { PlanFeatureKey } from '@/api/billing.api';

/**
 * ============================================================
 *  THE NAV SOURCE OF TRUTH
 * ============================================================
 *
 * Every destination in the shell is declared here, once. The
 * desktop rail, the collapsed icon rail, the mobile drawer and the
 * mobile tab bar all render from this same array — which is what
 * stops them drifting apart. (In the previous shell the mobile
 * bottom nav and the desktop sidebar were separate JSX trees, and
 * had already diverged.)
 *
 * To add a destination: add an entry. Do not add JSX to a renderer.
 * ============================================================
 */

/**
 * A row's icon: a lucide icon, or one of the DentaCares set
 * (`components/ds/icons/DentaCaresIcons`), which the top-level destinations
 * use. Both draw in `currentColor`, so the row's state colours them.
 */
export type NavIcon = ComponentType<{
  className?: string;
  'aria-hidden'?: boolean;
}>;

/** A permission gate: `hasPermission(permissions, resource, VIEW)`. */
export type Resource = string | null;

/** Clinic roles, for the few items gated by role rather than permission. */
export type ClinicRole =
  | 'ADMIN'
  | 'DOCTOR'
  | 'RECEPTIONIST'
  | 'ASSISTANT'
  | 'HYGIENIST'
  | 'OTHER';

/**
 * Live counters the shell resolves and injects by name.
 * `inventorySuggestions` is the count of pending usage suggestions — shown
 * only when non-zero, so an Inventory with nothing to suggest is silent.
 */
export type NavCounter =
  | 'pending'
  | 'chat'
  | 'inventorySuggestions'
  | 'stockTransfers';

export interface NavLeaf {
  kind: 'link';
  /** Stable key, also used as the localStorage key for group state. */
  id: string;
  labelKey: string;
  /** Fallback when the i18n key is missing. */
  labelFallback?: string;
  /** Shorter label for the mobile tab bar, where ~70px fits one word. */
  shortLabelKey?: string;
  icon: NavIcon;
  href: string;
  /** Permission resource that gates visibility. `null` = always visible. */
  resource?: Resource;
  /** Roles that may see this. Omitted = all roles. */
  roles?: ClinicRole[];
  /**
   * Extra path prefixes that should keep this item active. The href
   * itself is always matched as a prefix, so this is only for routes
   * that live elsewhere in the URL tree (e.g. Inventory owning
   * `/dashboard/materials`).
   */
  alsoActiveFor?: string[];
  /** Matches the href exactly instead of as a prefix. For index routes. */
  exact?: boolean;
  /** Named counter to render as a badge. Resolved by the renderer. */
  badge?: NavCounter;
  /**
   * Render only while `badge` is above zero. For entries that exist to act on
   * something — they would otherwise sit in the sidebar reporting nothing.
   */
  hideWhenZero?: boolean;
  /**
   * Only for a clinic with more than one branch the user can work in. A page
   * that compares branches has nothing to show a single-branch clinic.
   */
  multiBranchOnly?: boolean;
  /**
   * A plan feature the destination belongs to. Without it on the plan the
   * entry stays visible with a lock and opens the upgrade panel: hiding it
   * would hide the reason to upgrade.
   */
  feature?: PlanFeatureKey;
  /** Set by `useVisibleNav` when the plan lacks `feature`. */
  locked?: boolean;
}

export interface NavGroup {
  kind: 'group';
  id: string;
  labelKey: string;
  labelFallback?: string;
  icon: NavIcon;
  resource?: Resource;
  roles?: ClinicRole[];
  /** Path prefixes that mark the whole group active + auto-expand it. */
  activePrefixes: string[];
  /** Optional sub-headings inside the flyout, for long groups. */
  children: (NavLeaf | NavSubheading)[];
  badge?: NavCounter;
  /** Applies to every child; see NavLeaf.feature. */
  feature?: PlanFeatureKey;
  /** Set by `useVisibleNav` when every child is locked. */
  locked?: boolean;
}

export interface NavSubheading {
  kind: 'subheading';
  labelKey: string;
  labelFallback?: string;
}

export type NavNode = NavLeaf | NavGroup;

/** Convenience constructor keeps the table below readable. */
const link = (l: Omit<NavLeaf, 'kind'>): NavLeaf => ({ kind: 'link', ...l });

/*
 * The `heading()` constructor lived here and no longer does: nothing uses a
 * subheading now.
 *
 * The Finance group carried three of them — "Capital & Assets", "Statements",
 * "Payroll" — which was one level of hierarchy too many inside a flyout, and
 * it hid things. Statements became a real group (its Profit & Loss was not in
 * the sidebar at all) and has since been folded into Reports, Payroll is a
 * real group (its My Earnings was invisible to every clinician), and the two
 * capital pages read fine without a label over them.
 *
 * `NavSubheading` stays in the types and the renderers still handle it, so
 * reintroducing one is an entry in this file rather than a refactor. If a
 * group ever gets long enough to need dividing again, prefer asking whether it
 * should be two groups.
 */

/* ── Section 1: clinical day-to-day ───────────────────────── */

const CLINICAL: NavNode[] = [
  link({
    id: 'home',
    labelKey: 'nav.myHome',
    icon: DcCategoryClinicIcon,
    href: '/dashboard/home',
    resource: 'dashboard',
    exact: true,
  }),
  link({
    id: 'queue-reception',
    labelKey: 'nav.receptionQueue',
    shortLabelKey: 'nav.queueTab',
    icon: DcCategoryFrontDeskIcon,
    href: '/dashboard/queue/receptionist',
    resource: 'reservations',
    roles: ['RECEPTIONIST', 'ADMIN', 'OTHER'],
    exact: true,
  }),
  link({
    id: 'queue-doctor',
    labelKey: 'nav.doctorQueue',
    shortLabelKey: 'nav.queueTab',
    icon: DcCategoryAtChairIcon,
    href: '/dashboard/queue/doctor',
    resource: 'reservations',
    roles: ['DOCTOR', 'ASSISTANT', 'HYGIENIST', 'ADMIN', 'OTHER'],
    exact: true,
  }),
  link({
    id: 'patients',
    labelKey: 'nav.patients',
    icon: DcPatientFileIcon,
    href: '/dashboard/patients',
    resource: 'patients',
  }),
  {
    kind: 'group',
    id: 'reservations',
    labelKey: 'nav.reservations',
    icon: DcAppointmentsIcon,
    resource: 'reservations',
    badge: 'pending',
    activePrefixes: [
      '/dashboard/reservations',
      '/dashboard/waitlist',
      '/dashboard/recalls',
    ],
    children: [
      link({
        id: 'reservations-all',
        labelKey: 'reservations.calendarNav',
        labelFallback: 'Calendar',
        icon: DcCalendarIcon,
        href: '/dashboard/reservations',
        exact: true,
      }),
      link({
        id: 'reservations-list',
        labelKey: 'reservations.listNav',
        labelFallback: 'Appointment list',
        icon: DcAppointmentListIcon,
        href: '/dashboard/reservations/list',
        exact: true,
      }),
      link({
        id: 'reservations-pending',
        labelKey: 'reservations.reservationRequests',
        labelFallback: 'Reservation Requests',
        icon: DcAppointmentRequestsIcon,
        href: '/dashboard/reservations/pending',
        badge: 'pending',
      }),
      link({
        id: 'waitlist',
        labelKey: 'waitlist.title',
        labelFallback: 'Waitlist',
        icon: DcWaitlistIcon,
        href: '/dashboard/waitlist',
      }),
      link({
        id: 'recalls',
        labelKey: 'recall.title',
        labelFallback: 'Recalls',
        icon: DcRecallsIcon,
        href: '/dashboard/recalls',
      }),
    ],
  },
  {
    kind: 'group',
    id: 'labs',
    labelKey: 'nav.labs',
    feature: 'lab_cases',
    icon: DcLabTrackingIcon,
    resource: 'lab_requests',
    activePrefixes: ['/dashboard/labs', '/dashboard/lab-requests'],
    children: [
      link({
        id: 'lab-requests',
        labelKey: 'nav.labRequests',
        icon: DcLabRequestsIcon,
        href: '/dashboard/labs/requests',
        alsoActiveFor: ['/dashboard/lab-requests'],
      }),
      link({
        id: 'labs-list',
        labelKey: 'nav.labsList',
        icon: DcLabPartnerIcon,
        href: '/dashboard/labs/list',
        // A partner's page lives at /labs/partners/:id, beside the list.
        alsoActiveFor: ['/dashboard/labs/partners'],
      }),
    ],
  },
  // Staff chat. The badge is the unread count across every conversation.
  link({
    id: 'chat',
    labelKey: 'nav.chat',
    feature: 'staff_chat',
    icon: DcChatIcon,
    href: '/dashboard/chat',
    resource: 'chat',
    badge: 'chat',
  }),
];

/* ── Section 2: money ─────────────────────────────────────── */

const FINANCE: NavNode[] = [
  {
    kind: 'group',
    id: 'insurance',
    labelKey: 'nav.insurance',
    feature: 'insurance',
    icon: DcInsuranceClaimsIcon,
    resource: 'finance',
    activePrefixes: ['/dashboard/insurance'],
    children: [
      link({
        id: 'insurance-overview',
        labelKey: 'nav.insuranceOverview',
        icon: DcInsuranceOverviewIcon,
        href: '/dashboard/insurance',
        exact: true,
      }),
      link({
        id: 'insurance-claims',
        labelKey: 'nav.insuranceClaims',
        icon: DcClaimsIcon,
        href: '/dashboard/insurance/claims',
      }),
      link({
        id: 'insurance-batches',
        labelKey: 'nav.insuranceBatches',
        icon: DcBatchesIcon,
        href: '/dashboard/insurance/batches',
      }),
      link({
        id: 'insurance-companies',
        labelKey: 'nav.insuranceCompanies',
        icon: DcCompaniesPlansIcon,
        href: '/dashboard/insurance/companies',
      }),
    ],
  },
  {
    kind: 'group',
    id: 'finance',
    labelKey: 'nav.finance',
    icon: DcFinanceAccountsIcon,
    resource: 'finance',
    // Listed page by page rather than as `/dashboard/finance`: Profit & Loss
    // and the Monthly Financial Report keep their `/dashboard/finance/…` URLs
    // but sit under Reports now, and a bare prefix would light both groups up
    // at once (Payroll left here for the same reason). The Overview root is
    // covered by its own leaf — a group is active whenever one of its links
    // is. Add a new Finance page's path here when you add its link.
    activePrefixes: [
      '/dashboard/finance/income',
      '/dashboard/finance/expenses',
      '/dashboard/finance/payments',
      '/dashboard/finance/invoices',
      '/dashboard/finance/transactions',
      '/dashboard/finance/instalments',
      '/dashboard/finance/accounts',
      '/dashboard/finance/assets',
      '/dashboard/finance/owner-equity',
    ],
    children: [
      link({
        id: 'finance-overview',
        labelKey: 'nav.overview',
        labelFallback: 'Overview',
        icon: DcFinanceOverviewIcon,
        href: '/dashboard/finance',
        exact: true,
      }),
      link({
        id: 'finance-income',
        labelKey: 'nav.income',
        icon: DcIncomeIcon,
        href: '/dashboard/finance/income',
        // Invoices open from the Income list but live at /finance/invoices.
        alsoActiveFor: ['/dashboard/finance/invoices'],
      }),
      link({
        id: 'finance-expenses',
        labelKey: 'nav.expenses',
        icon: DcExpenseIcon,
        href: '/dashboard/finance/expenses',
      }),
      link({
        id: 'finance-accounts',
        labelKey: 'nav.accounts',
        labelFallback: 'Accounts',
        icon: DcAccountsIcon,
        href: '/dashboard/finance/accounts',
      }),
      link({
        id: 'finance-transactions',
        labelKey: 'nav.transactions',
        labelFallback: 'Transactions',
        icon: DcTransactionsIcon,
        href: '/dashboard/finance/transactions',
      }),
      link({
        id: 'finance-instalments',
        labelKey: 'nav.instalments',
        labelFallback: 'Instalments',
        icon: DcInstalmentsIcon,
        href: '/dashboard/finance/instalments',
      }),

      // The "Capital & Assets" subheading is gone. Two low-frequency
      // owner-level pages did not need a heading of their own inside a flyout
      // that already had two more; they read as one idea — what the clinic
      // owns long-term, and who funded it.
      link({
        id: 'finance-assets',
        labelKey: 'nav.assets',
        labelFallback: 'Equipment & Assets',
        icon: DcEquipmentAssetsIcon,
        href: '/dashboard/finance/assets',
      }),
      link({
        id: 'finance-equity',
        labelKey: 'nav.ownerEquity',
        labelFallback: 'Owner & Loans',
        icon: DcOwnerLoansIcon,
        href: '/dashboard/finance/owner-equity',
      }),
      // Practice Health used to sit here. It is the Reports root now — its
      // metrics are half operational, and Reports owned a nav slot while
      // computing nothing. The old path redirects.
    ],
  },
  /**
   * Payroll, lifted out of the Finance group.
   *
   * `My Earnings` deliberately carries no `resource` — seeing your own pay is
   * not the same right as seeing the clinic's. But it sat inside a group whose
   * `resource` is `finance`, and `useVisibleNav` gates a group BEFORE its
   * children, so the whole branch vanished for anyone without `finance:view`.
   * The result: a dentist could not reach their own payslip from the sidebar
   * at all — the route worked, but only if you typed the URL.
   *
   * This group has no `resource` of its own for exactly that reason. The
   * Payroll link inside it keeps `payroll`, so a clinician sees one entry and
   * an admin sees two; the empty-group pruning in `useVisibleNav` never fires
   * because `My Earnings` is always visible.
   */
  {
    kind: 'group',
    id: 'payroll',
    labelKey: 'nav.payroll',
    labelFallback: 'Payroll',
    icon: DcPayrollCommissionIcon,
    feature: 'payroll',
    activePrefixes: ['/dashboard/payroll', '/dashboard/my-earnings'],
    children: [
      link({
        id: 'payroll',
        labelKey: 'nav.payrollRuns',
        labelFallback: 'Payroll runs',
        icon: Banknote,
        href: '/dashboard/payroll',
        resource: 'payroll',
      }),
      // No `resource`. The API resolves the staff id from the token and
      // refuses outright when the session has none, so this cannot show
      // anyone else's pay.
      link({
        id: 'my-earnings',
        labelKey: 'nav.myEarnings',
        labelFallback: 'My Earnings',
        icon: Wallet,
        href: '/dashboard/my-earnings',
      }),
    ],
  },
  /**
   * Reports, named by the question each one answers.
   *
   * The old list paired "Expenses" with "Expense Summary" and "Cash
   * Collection" with "Transactions" — differences nobody could guess from the
   * label. Titles here follow the Balance Sheet's voice ("What your clinic
   * owns, owes, and is worth"): say what the reader will find out.
   *
   * Two reports that were routed and permission-gated but in no menu — A/R
   * Aging and Patient Statement — are listed now. They were reachable only
   * from a card grid on the Reports root, which is why nobody found them.
   *
   * Patient Risk has moved to Operations. It is a loyalty and no-show report
   * gated on `analytics`, not `reports`; it sat here purely because Reports
   * was where read-only pages accumulated.
   */
  {
    kind: 'group',
    id: 'reports',
    labelKey: 'nav.reports',
    labelFallback: 'Reports',
    icon: DcReportsAnalyticsIcon,
    resource: 'reports',
    activePrefixes: [
      '/dashboard/reports',
      '/dashboard/finance/profit-loss',
      '/dashboard/finance/monthly-report',
    ],
    children: [
      link({
        id: 'reports-overview',
        labelKey: 'nav.overview',
        labelFallback: 'Overview',
        icon: DcOverviewIcon,
        href: '/dashboard/reports',
        exact: true,
      }),
      /*
       * The financial statements, moved here from the Statements group: the
       * P&L, the Balance Sheet (with where the branches stand with each
       * other), Cash Flow and the monthly report. They keep their
       * `/dashboard/finance/…` URLs so bookmarks and the Overview
       * drill-throughs still work.
       *
       * Both carry their own `resource`: the group is gated on `reports`, but
       * these read the ledger and stay gated on `finance` like their routes.
       */
      link({
        id: 'finance-pl',
        labelKey: 'nav.profitLoss',
        labelFallback: 'Profit & Loss',
        icon: DcProfitLossIcon,
        href: '/dashboard/finance/profit-loss',
        resource: 'finance',
        feature: 'financial_statements',
      }),
      link({
        id: 'finance-balance-sheet',
        labelKey: 'nav.balanceSheet',
        labelFallback: 'Balance Sheet',
        icon: DcBalanceSheetIcon,
        href: '/dashboard/finance/balance-sheet',
        resource: 'finance',
        feature: 'financial_statements',
      }),
      link({
        id: 'finance-cash-flow',
        labelKey: 'nav.cashFlow',
        labelFallback: 'Cash Flow',
        icon: DcCashFlowIcon,
        href: '/dashboard/finance/cash-flow',
        resource: 'finance',
        feature: 'financial_statements',
      }),
      link({
        id: 'finance-monthly',
        labelKey: 'nav.monthlyReport',
        labelFallback: 'Monthly Financial Report',
        icon: DcMonthlyFinancialReportIcon,
        href: '/dashboard/finance/monthly-report',
        resource: 'finance',
      }),
      // Renamed from "AR Aging". The ladder is the method, not the question.
      link({
        id: 'reports-receivables',
        labelKey: 'reports.moneyOwed',
        labelFallback: 'Money owed to you',
        icon: DcMoneyOwedToYouIcon,
        href: '/dashboard/reports/ar-aging',
      }),
      // Renamed from "Cash Collection Report".
      link({
        id: 'reports-cash',
        labelKey: 'reports.cashControl',
        labelFallback: 'Cash control',
        icon: DcCashControlIcon,
        href: '/dashboard/reports/cash-collection',
      }),
      // Renamed from "Expense Summary", which sat one line from "Expenses".
      link({
        id: 'reports-expense',
        labelKey: 'reports.runningCosts',
        labelFallback: 'What it costs to run',
        icon: DcWhatItCostsToRunIcon,
        href: '/dashboard/reports/expense-summary',
      }),
      // Renamed from "Treatment Profitability".
      link({
        id: 'reports-treatment',
        labelKey: 'reports.treatmentsThatPay',
        labelFallback: 'Which treatments make money',
        icon: DcWhichTreatmentsMakeMoneyIcon,
        href: '/dashboard/reports/treatment-profitability',
        feature: 'financial_statements',
      }),
      // Routed and permission-gated, but in no menu until now.
      link({
        id: 'reports-patient-statement',
        labelKey: 'reports.patientStatement',
        labelFallback: 'Patient statement',
        icon: DcPatientStatementIcon,
        href: '/dashboard/reports/patient-statement',
      }),

      /*
       * Still listed, pending their merge.
       *
       * Refunds folds into Cash control; Patient Income folds into Money owed
       * to you. Neither merge has landed, and removing a link before the
       * capability moves would lose the capability — the one thing this
       * consolidation is not allowed to do. They stay until their replacement
       * exists.
       *
       * The three removed from here — Pending Payments, Accounts Balance and
       * the KPI dashboard — each had a destination ready: an addressable
       * Income tab, the by-type subtotals now on Accounts, and Finance
       * Overview. All three routes redirect rather than 404.
       */
      link({
        id: 'reports-refunds',
        labelKey: 'reports.refunds',
        labelFallback: 'Refunds and corrections',
        icon: DcRefundsCorrectionsIcon,
        href: '/dashboard/reports/refunds',
      }),
      link({
        id: 'reports-patient-income',
        labelKey: 'reports.patientIncome',
        labelFallback: 'What each patient spent',
        icon: DcWhatEachPatientSpentIcon,
        href: '/dashboard/reports/patient-income',
      }),
      link({
        id: 'reports-production',
        labelKey: 'reports.production.title',
        labelFallback: 'Production by dentist',
        icon: DcProductionByDentistIcon,
        href: '/dashboard/reports/production',
        feature: 'payroll',
        // The commission base: the same right that opens staff pay.
        resource: 'staff_compensation',
      }),
      link({
        id: 'reports-offers',
        labelKey: 'reports.offers.title',
        labelFallback: 'Offers and loyalty',
        icon: DcOffersLoyaltyIcon,
        href: '/dashboard/reports/offers-loyalty',
        feature: 'loyalty_offers',
      }),
      link({
        id: 'reports-branches',
        labelKey: 'reports.branchComparison',
        labelFallback: 'Branches side by side',
        icon: DcBranchesSideBySideIcon,
        href: '/dashboard/reports/branches',
        feature: 'branch_reports',
        multiBranchOnly: true,
      }),
    ],
  },
];

/* ── Section 3: operations ────────────────────────────────── */

const OPERATIONS: NavNode[] = [
  {
    kind: 'group',
    id: 'inventory',
    labelKey: 'nav.inventory',
    feature: 'inventory',
    labelFallback: 'Inventory',
    icon: DcInventoryIcon,
    resource: 'inventory',
    badge: 'inventorySuggestions',
    activePrefixes: [
      '/dashboard/inventory',
      '/dashboard/materials',
      '/inventory/',
    ],
    children: [
      link({
        id: 'inv-stock',
        labelKey: 'nav.stockLevels',
        labelFallback: 'Stock Levels',
        icon: Layers,
        href: '/dashboard/inventory/stock-levels',
      }),
      // Present only while there is something to suggest; opens the panel
      // directly rather than the page.
      link({
        id: 'inv-suggestions',
        labelKey: 'nav.usageSuggestions',
        labelFallback: 'Usage suggestions',
        icon: Sparkles,
        href: '/dashboard/inventory/stock-levels?panel=suggestions',
        badge: 'inventorySuggestions',
        hideWhenZero: true,
      }),
      link({
        id: 'inv-purchases',
        labelKey: 'nav.stockPurchases',
        labelFallback: 'Stock Purchases',
        icon: ShoppingCart,
        href: '/dashboard/inventory/purchases',
      }),
      link({
        id: 'inv-transfers',
        labelKey: 'nav.stockTransfers',
        labelFallback: 'Stock transfers',
        icon: ArrowRightLeft,
        href: '/dashboard/inventory/transfers',
        badge: 'stockTransfers',
        hideWhenZero: true,
      }),
      link({
        id: 'inv-usage',
        labelKey: 'nav.usage',
        labelFallback: 'Usage',
        icon: LineChart,
        href: '/dashboard/inventory/usage',
        alsoActiveFor: ['/dashboard/inventory/consumption'],
      }),
      link({
        id: 'inv-count',
        labelKey: 'nav.stockCount',
        labelFallback: 'Stock Count',
        icon: ClipboardCheck,
        href: '/dashboard/inventory/stock-count',
      }),
      link({
        id: 'inv-suppliers',
        labelKey: 'nav.suppliers',
        labelFallback: 'Suppliers',
        icon: Truck,
        href: '/dashboard/inventory/suppliers',
      }),
    ],
  },
  /**
   * Analytics: the operational read-only surfaces, together.
   *
   * The dashboard sat loose at the top level and Patient Risk sat inside
   * Reports. Both are gated on `analytics`, neither is financial, and the two
   * answer adjacent questions about how the clinic is running. Grouping them
   * takes one item off the top level and puts Patient Risk somewhere a person
   * looking for it would think to look.
   */
  {
    kind: 'group',
    id: 'analytics',
    labelKey: 'nav.analytics',
    feature: 'clinic_analytics',
    labelFallback: 'Analytics',
    icon: DcProfitPerTreatmentIcon,
    resource: 'analytics',
    activePrefixes: [
      '/dashboard/analytics-dashboard',
      '/dashboard/referral-sources',
      '/dashboard/patient-risk-report',
    ],
    children: [
      link({
        id: 'analytics-dashboard',
        labelKey: 'nav.clinicPerformance',
        labelFallback: 'Clinic performance',
        icon: BarChart3,
        href: '/dashboard/analytics-dashboard',
      }),
      link({
        id: 'analytics-referral-sources',
        labelKey: 'nav.referralSources',
        labelFallback: 'Referral sources',
        icon: Megaphone,
        href: '/dashboard/referral-sources',
      }),
      link({
        id: 'analytics-patient-risk',
        labelKey: 'nav.patientRiskReport',
        labelFallback: 'Patient Risk Report',
        icon: ShieldAlert,
        href: '/dashboard/patient-risk-report',
      }),
    ],
  },
  /*
   * The activity log was a settings section, but it configures nothing —
   * people open it to find out who changed what — so it sits with the other
   * pages about how the clinic is running. Gated on `settings`, which is what
   * the audit API checks.
   */
  link({
    id: 'activity-log',
    labelKey: 'nav.activityLog',
    feature: 'activity_log',
    icon: Activity,
    href: '/dashboard/activity-log',
    resource: 'settings',
  }),
];

/**
 * The rail, in order. Sections are visual groupings with a heading;
 * they are not collapsible (the groups inside them are).
 */
export interface NavSection {
  id: string;
  labelKey: string;
  labelFallback: string;
  items: NavNode[];
}

export const NAV_SECTIONS: NavSection[] = [
  {
    id: 'clinical',
    labelKey: 'nav.sectionClinical',
    labelFallback: 'Clinical',
    items: CLINICAL,
  },
  {
    id: 'finance',
    labelKey: 'nav.sectionFinance',
    labelFallback: 'Finance',
    items: FINANCE,
  },
  {
    id: 'operations',
    labelKey: 'nav.sectionOperations',
    labelFallback: 'Operations',
    items: OPERATIONS,
  },
];

/**
 * Mobile tab bar. A deliberate SUBSET of the same nodes rather than a
 * parallel list — each entry names a node above, so a route change
 * there cannot leave the tab bar pointing somewhere stale.
 *
 * Candidates in priority order; the bar shows the first
 * `MOBILE_TAB_LIMIT` the user can open, and "More" holds the rest.
 * `queue` is the role's own queue (see `MOBILE_QUEUE_IDS`): the screen
 * front desk and chairside staff live in all day was two taps away
 * under "More".
 */
export const MOBILE_TAB_IDS = [
  'home',
  'queue',
  'patients',
  'reservations',
  'finance',
] as const;

/** Four destinations plus "More" — the Material / HIG ceiling for a tab bar. */
export const MOBILE_TAB_LIMIT = 4;

/** Which queue the `queue` tab opens, most specific first per role. */
export const MOBILE_QUEUE_IDS: Record<ClinicRole, readonly string[]> = {
  DOCTOR: ['queue-doctor', 'queue-reception'],
  ASSISTANT: ['queue-doctor', 'queue-reception'],
  HYGIENIST: ['queue-doctor', 'queue-reception'],
  RECEPTIONIST: ['queue-reception', 'queue-doctor'],
  ADMIN: ['queue-reception', 'queue-doctor'],
  OTHER: ['queue-reception', 'queue-doctor'],
};

export const SETTINGS_NODE = {
  kind: 'group' as const,
  id: 'settings',
  labelKey: 'nav.settings',
  icon: Settings,
  activePrefixes: ['/dashboard/settings'],
  children: [] as NavLeaf[],
};
